import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Navbar,
  NavbarBrand,
  Nav,
  NavItem,
  NavbarToggler,
  Collapse,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
} from 'reactstrap';
import { NavLink, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { ClientScopeSelector } from './ClientScopeSelector';
import { BookingService } from '../../services/BookingService';
import { UserContext } from '../../context/UserContext';
import { ToasterContext } from '../../context/ToasterContext';
import { AccountCenter } from '@sgmo/shared-components';

const ScrollableDropDownMenu = styled(DropdownMenu)`
  max-height: 30vh;
  overflow-y: scroll;
`;

export const Header: React.FC = () => {
  const intl = useIntl();
  const [collapsed, setCollapsed] = React.useState(true);
  const [bookingContracts, setBookingContracts] = React.useState<
    InstrumentContracts[]
  >([]);
  const [bookingContractSearch, setBookingContractSearch] = React.useState('');
  const abortController = new AbortController();
  const { globalContext } = React.useContext(UserContext);
  const { pushToastMessage } = React.useContext(ToasterContext);
  const history = useHistory();

  const openDeal = (dealId: string) => {
    history.push(`/trade-capture/${dealId}`);
  };

  const toggle = React.useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  React.useEffect(() => {
    BookingService.getContractDescriptions(
      abortController.signal,
      globalContext,
    )
      .then((contracts) => {
        setBookingContracts(contracts);
      })
      .catch((error) => {
        console.error('trade-capture: failed to retrieve contracts', error);
        pushToastMessage({
          body: { id: 'trade-capture.error-fetch-contract' },
          level: 'danger',
          title: { id: 'label.danger' },
        });
      });
  }, []);

  React.useEffect(() => {
    bookingContracts.forEach((bookingContract) => {
      if (bookingContract.Name.includes(bookingContractSearch)) {
        bookingContract.IsFilteredOut = false;
      } else {
        bookingContract.IsFilteredOut = true;
      }
    });
  }, [bookingContractSearch]);

  return (
    <Navbar
      expand="xl"
      className="d-flex justify-content-between align-content-center border-bottom"
    >
      <div className="d-block">
        <NavbarBrand
          href={(import.meta as any).env.SNOWPACK_PUBLIC_BASE_URL}
          className="mr-0"
        >
          <svg
            height="32"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
          >
            <g>
              <rect fill="#e60028" width="32" height="16" />
              <rect fill="#000" y="16" width="32" height="16" />
              <rect fill="#fff" x="6" y="15" width="20" height="2" />
            </g>
          </svg>
        </NavbarBrand>
      </div>

      <NavbarToggler onClick={toggle} className="mr-auto">
        <FormattedMessage id="menu.title" />
        <i className="icon icon-md text-secondary">arrow_drop_down</i>
      </NavbarToggler>

      <Collapse isOpen={!collapsed} navbar>
        <Nav navbar>
          <UncontrolledDropdown nav inNavbar>
            <DropdownToggle nav caret>
              New deal
            </DropdownToggle>
            <ScrollableDropDownMenu left>
              <div className="p-2">
                <Input
                  type="text"
                  className="form-control-md"
                  name="instrumentSearch"
                  id="instrumentSearch"
                  placeholder="Search..."
                  value={bookingContractSearch}
                  onChange={(e) => setBookingContractSearch(e.target.value)}
                />
              </div>
              <DropdownItem divider />
              {bookingContracts
                .filter(
                  (instrumentContracts) =>
                    instrumentContracts.IsFilteredOut === undefined ||
                    instrumentContracts.IsFilteredOut === false,
                )
                .map((instrumentContracts) => (
                  <DropdownItem className="p-2">
                    <NavLink
                      key={instrumentContracts.Name}
                      to={`/trade-capture/${instrumentContracts.Name}/${instrumentContracts.InstrumentContracts[0].Name}`}
                      className="nav-link"
                    >
                      {instrumentContracts.Name}
                    </NavLink>
                  </DropdownItem>
                ))}
            </ScrollableDropDownMenu>
          </UncontrolledDropdown>
          <NavItem>
            <NavLink
              key="Hardcoded_Leasing"
              to="/trade-capture/EquipmentLeasing/Leasing"
              className="nav-link"
            >
              New equipment leasing
            </NavLink>
          </NavItem>
          <NavItem>
            <Input
              type="text"
              className="form-control"
              name="openDeal"
              id="openDeal"
              placeholder="Open deal..."
              onKeyDown={(e) =>
                e.key === 'Enter' && openDeal(e.currentTarget.value)
              }
            />
          </NavItem>
        </Nav>
      </Collapse>

      <ClientScopeSelector />
      <AccountCenter
        appEnvironment={
          globalContext.appConfig?.sgmoEnvironment || 'production'
        }
        widgetEnvironment={
          globalContext.appConfig?.sgmoEnvironment || 'production'
        }
        locale={intl.locale}
      />

      <li className="d-none d-lg-block overflow-visible ml-3 nav-item">
        <svg width="145" height="32" className="overflow-visible">
          <path d="M0 10.448h1.879c.254 2.582 1.83 3.928 4.715 3.928 2.109 0 3.624-.958 3.624-2.86 0-2.086-1.83-2.583-4.57-3.177C2.91 7.745.655 6.97.655 4.097.655 1.576 2.739.061 5.842.061 9.661.06 11.236 2 11.624 4.812H9.806c-.279-2.182-1.685-3.26-3.927-3.26-2.243 0-3.43.884-3.43 2.46s1.709 2.243 3.987 2.691c2.97.594 5.673 1.321 5.673 4.752 0 2.86-2.327 4.46-5.612 4.46-4.279 0-6.327-2.388-6.497-5.467zM25.224 13.612h-.048c-1.115 1.552-2.752 2.327-4.752 2.327-4.29 0-7.163-3.26-7.163-7.89s2.533-8 7.333-8c3.54 0 5.721 1.915 6.206 4.909h-1.976c-.424-2.025-1.854-3.249-4.266-3.249-3.673 0-5.334 2.752-5.334 6.34 0 3.587 2.182 6.23 5.273 6.23 3.09 0 4.606-2.134 4.606-4.691v-.364h-4.594V7.552h6.51v8.06h-1.237zM39.067.364h4.351l2.158 7.66c.533 1.915.957 4.121.957 4.121h.049s.4-2.193.933-4.12L49.673.363h4.412v15.248H51.2v-8.74c0-1.236.133-3.15.133-3.15h-.048s-.34 1.793-.667 2.896L48 15.624h-2.97l-2.557-9.006c-.315-1.115-.667-2.897-.667-2.897h-.048s.133 1.915.133 3.152v8.751h-2.824zM60.206.364h3.2l5.382 15.248h-3.224l-1.019-3.248H59.03l-1.006 3.248h-3.127zM59.758 10h4.036l-1.37-4.388c-.254-.812-.618-2.351-.618-2.351h-.048s-.388 1.539-.643 2.351zM75.321 9.721h-2.509v5.891h-3.2V.364h6.703c1.77 0 3.091.509 3.988 1.43a4.545 4.545 0 0 1 1.37 3.309c0 2.133-1.115 3.624-3.03 4.267l3.818 6.254h-3.6zm-2.509-2.509h3.2c1.624 0 2.473-.751 2.473-2.085 0-1.333-.897-2.072-2.437-2.072h-3.236zM83.321.364h3.091v6.072L92.28.352h3.842L90.158 6.46l6.351 9.151h-3.624l-4.91-7.127-1.55 1.576v5.551H83.32zM97.297.364h11.224V2.97h-8.133v3.406h7.127V8.92h-7.127v4.121h8.194v2.582H97.297zM109.297.364h12.267V2.97h-4.582v12.642h-3.091V2.97h-4.594zM121.879 10.897h3.03c.218 1.83 1.26 2.558 3.43 2.558 1.576 0 2.97-.558 2.97-1.964s-1.454-1.794-3.782-2.327c-2.727-.619-5.224-1.346-5.224-4.522 0-3.175 2.449-4.57 5.842-4.57 3.394 0 5.649 1.686 5.891 4.752h-2.97c-.169-1.54-1.369-2.303-2.945-2.303-1.576 0-2.751.703-2.751 1.794 0 1.091 1.066 1.6 3.333 2.085 3.14.679 5.697 1.43 5.697 4.715 0 3.091-2.497 4.824-5.927 4.824-4.146.013-6.485-1.818-6.594-5.042z" />
          <path fill="#E60028" d="M31.43 0h1.697v16H31.43z" />
          <text className="font-weight-medium" y="32">
            <FormattedMessage id="home.app-title" />
          </text>
        </svg>
      </li>
    </Navbar>
  );
};
